import api from '../../api/config';
import { STATUSES } from '../../api/endpoints';
import { extractList } from '../../utils/response';
import { getCachedStatusesPayload, setCachedStatusesPayload } from '../../utils/lookupSessionCache';

export const IMPORT_BUSINESS_STATUS_MODEL = 'ImportBusiness';

export const IMPORT_BUSINESS_RELATIONS = [
  'businessCategory',
  'state.country',
  'status',
  'author',
  'assignee',
];

export async function fetchImportBusinessStatusOptions() {
  const params = {
    paginate: 0,
    'conditions[model]': IMPORT_BUSINESS_STATUS_MODEL,
  };
  const cached = getCachedStatusesPayload(params);
  if (cached != null) return extractList(cached);
  const { data } = await api.get(STATUSES.LIST, { params });
  setCachedStatusesPayload(params, data);
  return extractList(data);
}

export function buildImportBusinessRelationParams() {
  return IMPORT_BUSINESS_RELATIONS.reduce((acc, relation, index) => {
    acc[`relations[${index}]`] = relation;
    return acc;
  }, {});
}

export function buildImportBusinessListParams({
  page = 1,
  per_page = 15,
  search = '',
  sort_by = 'created_at',
  sort_direction = 'desc',
  filters = {},
} = {}) {
  const params = {
    paginate: 1,
    page,
    per_page,
    sort_by,
    sort_direction,
    ...buildImportBusinessRelationParams(),
    ...(search ? { search } : {}),
  };

  if (filters.status_id) {
    params.status_id = filters.status_id;
  }

  if (filters.business_category_id) {
    params.business_category_id = filters.business_category_id;
  }

  if (filters.state_id) {
    params.state_id = filters.state_id;
  }

  const {
    status_id: _statusId,
    business_category_id: _businessCategoryId,
    state_id: _stateId,
    ...otherFilters
  } = filters;
  Object.entries(otherFilters).forEach(([key, value]) => {
    if (value !== '' && value != null) {
      params[`filters[${key}]`] = value;
    }
  });

  return params;
}

export function resolveImportBusinessId(row) {
  return row?.id ?? row?.uuid ?? null;
}

export function getImportBusinessName(row) {
  return row?.name ?? '—';
}

export function getImportBusinessCategory(row) {
  return row?.business_category?.name ?? row?.businessCategory?.name ?? '—';
}

export function getImportBusinessStatus(row) {
  return row?.status?.name ?? row?.status?.label ?? row?.status_name ?? '—';
}

export function getImportBusinessState(row) {
  return row?.state?.name ?? row?.state_name ?? '—';
}

export function getImportBusinessCountry(row) {
  return row?.state?.country?.name ?? row?.country?.name ?? row?.country_name ?? '—';
}

export function getImportBusinessAuthor(row) {
  return row?.author?.name ?? row?.author?.username ?? '—';
}

export function getImportBusinessAssignee(row) {
  const assignee = row?.assignee;
  if (!assignee || typeof assignee !== 'object') return '—';
  return assignee.name ?? assignee.username ?? '—';
}

export function formatImportBusinessDate(value) {
  if (!value) return '—';
  const raw = String(value).trim();
  if (!raw) return '—';
  const parsed = new Date(raw.includes('T') ? raw : raw.replace(' ', 'T'));
  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toLocaleString();
}

export function ellipsizeImportBusiness(value, max = 36) {
  if (value === undefined || value === null || value === '') return '—';
  const text = String(value);
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

export function displayImportBusinessValue(value) {
  if (value === undefined || value === null || value === '') return '—';
  return String(value);
}

export function resolveImportBusinessStatusId(row) {
  return row?.status?.id ?? row?.status_id ?? '';
}

export function resolveImportBusinessStateId(row) {
  return row?.state?.id ?? row?.state_id ?? '';
}

export function resolveImportBusinessCategoryId(row) {
  return row?.business_category?.id ?? row?.businessCategory?.id ?? row?.business_category_id ?? '';
}

/** Match dropdown option when API ids are string/number mismatched */
export function findImportBusinessOption(options, id, relationFallback) {
  if (id !== '' && id != null) {
    const found = (options || []).find(
      (option) => String(option?.id) === String(id)
    );
    if (found) return found;
  }

  const fallback = relationFallback;
  if (fallback?.id) {
    return {
      id: fallback.id,
      name: fallback.name ?? fallback.label ?? '',
    };
  }

  return null;
}

export function buildImportBusinessFormData(fields, { alwaysInclude = [] } = {}) {
  const formData = new FormData();
  formData.append('_method', 'PUT');
  Object.entries(fields).forEach(([key, value]) => {
    const forceInclude = alwaysInclude.includes(key);
    if (forceInclude || (value !== undefined && value !== null && value !== '')) {
      formData.append(key, forceInclude ? (value ?? '') : value);
    }
  });
  return formData;
}

export function buildAssignImportBusinessFormData(assigneeId, businessIds = []) {
  const formData = new FormData();
  formData.append('assignee_id', assigneeId);
  businessIds.forEach((id, index) => {
    formData.append(`import_businesses[${index}]`, id);
  });
  return formData;
}

export function normalizeImportBusinessStatusName(name) {
  return (name || '').toLowerCase().replace(/[\s_-]+/g, ' ').trim();
}

export function isTransferredImportBusinessStatus(statusOptions, statusId) {
  if (!statusId) return false;
  const status = (statusOptions || []).find(
    (s) => String(s.id) === String(statusId)
  );
  return normalizeImportBusinessStatusName(status?.name) === 'transferred';
}

export function isLeadGenUser(user) {
  const roleName = (user?.roles?.[0]?.name || user?.role?.name || '').toLowerCase();
  return roleName === 'lead gen';
}

/** Maps import business row into Create Lead form fields */
export function mapImportBusinessToLeadFormPrefill(business, user) {
  const name = business?.name ?? '';
  const category =
    business?.business_category?.name ??
    business?.businessCategory?.name ??
    '';
  const countryId =
    business?.state?.country?.id ??
    business?.country?.id ??
    '';
  const address = business?.address ?? '';

  return {
    dba_name: name,
    name,
    phone: business?.phone ?? '',
    address,
    description: business?.description ?? '',
    business_category: category,
    country_id: countryId,
    legal_business_name: name,
    dba_address: address,
    legal_business_address: address,
    import_business_id: resolveImportBusinessId(business) ?? '',
    assignee_ids: user?.id ? [user.id] : [],
  };
}

/** Detail view field definitions */
export const IMPORT_BUSINESS_DETAIL_FIELDS = [
  { label: 'Status', get: getImportBusinessStatus },
  { label: 'Business Category', get: getImportBusinessCategory },
  { label: 'Name', get: getImportBusinessName },
  { label: 'Phone', get: (row) => displayImportBusinessValue(row?.phone) },
  { label: 'Address', get: (row) => displayImportBusinessValue(row?.address) },
  { label: 'State', get: getImportBusinessState },
  { label: 'Country', get: getImportBusinessCountry },
  { label: 'Is Lead', get: (row) => displayImportBusinessValue(row?.is_lead) },
  { label: 'Source', get: (row) => displayImportBusinessValue(row?.source) },
  { label: 'Sheet Name', get: (row) => displayImportBusinessValue(row?.sheet_name) },
  { label: 'Assigned At', get: (row) => displayImportBusinessValue(row?.assigned_at) },
  { label: 'Description', get: (row) => displayImportBusinessValue(row?.description) },
  { label: 'Author', get: getImportBusinessAuthor },
  { label: 'Created At', get: (row) => formatImportBusinessDate(row?.created_at) },
];
